import { Item as Accordion, Content, Header, Trigger } from '@radix-ui/react-accordion';
import {
  selectDefaultSistemaValue,
  selectIsPuntataEnabled,
  selectNumEsitiByTicketType,
  selectPuntataPerScommessaByType,
  selectSistemiDaGiocareByType,
  selectTipo,
} from 'features/carrello/carrelloSelectors';

import styles from './CarrelloCombinazioniItem.module.scss';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useEffect, useMemo } from 'react';

import { CarrelloCombinazioniContent } from '../carrelloCombinazioniContent';
import { CarrelloEditQuotaInputSistema } from 'features/carrello/carrelloEditQuotaInputSistema';
import { SimpleCheckbox } from 'components/inputs/simpleCheckbox';
import { getSliceByTipoTicket } from 'features/carrello/utils';
import { isSnaiSite } from 'utility/constant';
import { puntataPerScommesseSistemaRemoved } from 'features/sport/components/sportTicket/sportTicketSlice';
import { selectedSistema } from 'features/carrello/carrelloSlice';
import { updatePuntataSistema } from 'features/carrello/carrelloEditQuotaInputSistema/actions';
import { Icon, IconsEnum } from 'components/Icons';
import { useSistema } from 'features/carrello/hooks';
import { useGetLabelsByKeys } from 'hooks/useLingUI';

export type CarrelloCombinazioniItemProps = {
  id: string;
  handleAccordionToggle: (_id: string, _isEnabled: boolean) => void;
};

export const CarrelloCombinazioniItem = ({ id, handleAccordionToggle }: CarrelloCombinazioniItemProps) => {
  const dispatch = useAppDispatch();

  const tipo = useTypedSelector(selectTipo);
  const { actions } = getSliceByTipoTicket(tipo);
  const sistemiDaGiocare = useTypedSelector((state) => selectSistemiDaGiocareByType(state, tipo));
  const puntataPerScommessa = useTypedSelector((state) => selectPuntataPerScommessaByType(state, tipo));
  const defaultSystemBetValue = useTypedSelector(selectDefaultSistemaValue);
  const numEsiti = useTypedSelector((state) => selectNumEsitiByTicketType(state, tipo));

  const { stakePerBet, numCombinazioni, numScommesseSelezionabiliCorrente } = useSistema(id) ?? {};
  const isEnabled = useTypedSelector((state) => selectIsPuntataEnabled(state, tipo, id));
  const [labelSingola, labelDoppia, labelTripla, labelUpla] = useGetLabelsByKeys([
    'scommessa-singola',
    'scommessa-doppia',
    'scommessa-tripla',
    'scommessa-upla',
  ]);

  const labelNumScommessa = useMemo(() => {
    switch (numScommesseSelezionabiliCorrente) {
      case 1:
        return labelSingola;
      case 2:
        return labelDoppia;
      case 3:
        return labelTripla;
      default:
        return `${numScommesseSelezionabiliCorrente}-${labelUpla}`;
    }
  }, [labelDoppia, labelSingola, labelTripla, labelUpla, numScommesseSelezionabiliCorrente]);

  const handleChangeCheckbox = (_element, isChecked: boolean) => {
    let newSistemi: Array<string> = isChecked
      ? [...sistemiDaGiocare, id]
      : sistemiDaGiocare.filter((sistema) => sistema !== id);
    dispatch(actions.sistemiDaGiocareUpdated(newSistemi));
    dispatch(selectedSistema(id));
    const sistemiDaRimuovere = Object.keys(puntataPerScommessa).filter((key) => {
      return !newSistemi.includes(key);
    });
    if (sistemiDaRimuovere.length > 0) {
      sistemiDaRimuovere.forEach((sistema) => {
        dispatch(puntataPerScommesseSistemaRemoved(sistema));
      });
    }
    if (!isSnaiSite) {
      const betAmount = stakePerBet;
      const amount = betAmount ? betAmount / 100 : 0.5;
      newSistemi.forEach((sistema) => {
        dispatch(updatePuntataSistema(amount, true, sistema));
      });
    } else {
      newSistemi.forEach((sistema) => {
        if (!puntataPerScommessa[sistema]) {
          dispatch(updatePuntataSistema(defaultSystemBetValue, true, sistema, true));
        }
      });
    }

    handleAccordionToggle(id, isChecked);
  };

  const handleChangeQuotaInput = (_e: React.MouseEvent<HTMLButtonElement>) => {
    handleChangeCheckbox(_e, true);
  };

  useEffect(() => {
    if (sistemiDaGiocare && puntataPerScommessa) {
      dispatch(actions.ticketUpdatedBySistemi(sistemiDaGiocare));
    }
  }, [actions, dispatch, puntataPerScommessa, sistemiDaGiocare, numEsiti]);

  return (
    <Accordion value={id} className={styles.item}>
      <Header className={styles.header}>
        <div className={styles.checkboxContainer}>
          <SimpleCheckbox
            handleFilterChange={handleChangeCheckbox}
            value={id}
            checked={sistemiDaGiocare.includes(id)}
            className={styles.checkbox}
            label={labelNumScommessa}
          />
          <span className={styles.text}>{`(x${numCombinazioni})`}</span>
        </div>
        <div className={styles.flex}>
          <CarrelloEditQuotaInputSistema
            sistema={true}
            idSistema={id}
            isDisabled={!isEnabled}
            handleChangeQuotaInput={handleChangeQuotaInput}
          />

          <Trigger className={!isEnabled ? styles.disabledTrigger : styles.trigger} disabled={!isEnabled}>
            <Icon iconId={IconsEnum.CHEVRON_DOWN} className={styles.iconTrigger} color="var(--color-primary)" />
          </Trigger>
        </div>
      </Header>
      <Content className={styles.content}>
        <CarrelloCombinazioniContent idSistema={id} />
      </Content>
    </Accordion>
  );
};
